import { Injectable } from "@angular/core";
import {
	WalletBalanceState,
	WalletsBalancesStateService
} from "@modules/+new/core/wallets-balances/domain/+state/wallets-balances.state.service";
import { WalletsBalancesRemoteService } from "@modules/+new/core/wallets-balances/domain/infrastructure/wallets-balances-remote.service";
import { Observable } from "rxjs";
import { map, switchMap, tap } from "rxjs/operators";
import { WalletBalance } from "@modules/+new/core/wallets-balances/domain/entities/wallet-balance.model";
import { WalletsBalanceStatus } from "@modules/+new/core/wallets-balances/domain/entities/wallets-balance-status.model";

@Injectable({
	providedIn: "root"
})
export class WalletsBalancesFacadeService {
	constructor(
		private readonly state: WalletsBalancesStateService,
		private readonly remote: WalletsBalancesRemoteService
	) {}

	public getFiatWalletsBalances$(): Observable<WalletBalance[]> {
		if (!this.state.isStateLoaded()) {
			return this.refreshBalances$().pipe(switchMap(() => this.state.selectFiatWallets()));
		}

		return this.state.selectFiatWallets();
	}

	public getCryptoWalletsBalances$(): Observable<WalletBalance[]> {
		if (!this.state.isStateLoaded()) {
			return this.refreshBalances$().pipe(switchMap(() => this.state.selectCryptoWallets()));
		}

		return this.state.selectCryptoWallets();
	}

	public getCryptoBalancesStatus$(): Observable<WalletsBalanceStatus> {
		if (!this.state.isStateLoaded()) {
			return this.refreshBalances$().pipe(switchMap(() => this.state.selectCryptoWalletsStatus()));
		}

		return this.state.selectCryptoWalletsStatus();
	}

	public getFiatBalancesStatus$(): Observable<WalletsBalanceStatus> {
		if (!this.state.isStateLoaded()) {
			return this.refreshBalances$().pipe(switchMap(() => this.state.selectFiatWalletsStatus()));
		}

		return this.state.selectFiatWalletsStatus();
	}

	public refreshBalances$(): Observable<Omit<WalletBalanceState, "loaded">> {
		return this.remote
			.getBalances$()
			.pipe(tap((balances: Omit<WalletBalanceState, "loaded">) => this.state.setWallets(balances)));
	}

	public getFiatWalletBalance$(walletGuid: string): Observable<WalletBalance> {
		return this.getFiatWalletsBalances$().pipe(
			map((wallets: WalletBalance[]) => wallets.find((wallet: WalletBalance) => wallet.walletGuid === walletGuid))
		);
	}

	public getCryptoWalletBalance$(walletGuid: string): Observable<WalletBalance> {
		return this.getCryptoWalletsBalances$().pipe(
			map((wallets: WalletBalance[]) => wallets.find((wallet: WalletBalance) => wallet.walletGuid === walletGuid))
		);
	}
}
